import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Heart, Moon, Shield, CheckCircle, ChevronDown, Zap, Users2, Settings, Smartphone, Star, Info, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import nasalMeasurement from '@/assets/nasal-measurement.jpg';
import oronasalMeasurement from '@/assets/oronasal-measurement.jpg';
export default function Introduccion() {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const beneficiosClave = [{
    icon: <Moon className="h-5 w-5 text-primary" />,
    titulo: 'Mejor descanso',
    descripcion: 'Elimina interrupciones nocturnas'
  }, {
    icon: <Heart className="h-5 w-5 text-primary" />,
    titulo: 'Salud cardiovascular',
    descripcion: 'Reduce riesgos cardíacos'
  }, {
    icon: <Shield className="h-5 w-5 text-primary" />,
    titulo: 'Más energía diaria',
    descripcion: 'Despierta renovado'
  }];
  const componentesEsenciales = [{
    id: 'equipo',
    titulo: 'Equipo CPAP Automático',
    subtitle: 'El corazón de tu terapia',
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YH680B_1.webp',
    descripcionCorta: 'Genera la presión de aire constante que mantiene tus vías respiratorias abiertas',
    caracteristicas: ['Presión automática que se ajusta a tus necesidades', 'Funcionamiento silencioso', 'Conectividad para monitoreo remoto']
  }, {
    id: 'mascarilla',
    titulo: 'Mascarilla CPAP',
    subtitle: 'Tu conexión con la terapia',
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YN-03%20(Nasal).webp',
    descripcionCorta: 'La interfaz personalizada que conecta el equipo contigo de forma cómoda y efectiva',
    tipos: [{
      nombre: 'Nasal',
      imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YN-03%20(Nasal).webp',
      descripcion: 'Solo cubre la nariz. Ideal para principiantes.',
      recomendada: 'Más cómoda'
    }, {
      nombre: 'Oronasal',
      imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YF-02%20(Oronasal).webp',
      descripcion: 'Cubre nariz y boca. Para respiradores bucales.',
      recomendada: 'Cobertura completa'
    }]
  }];
  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };
  return <>
      <div className="container px-3 sm:px-4 py-4 sm:py-8 pb-24">
        <div className="max-w-4xl mx-auto">
          
          {/* Page Header */}
          <div className="mb-0 sm:mb-6 p-4 text-center">
            <h1 className="text-2xl font-bold text-primary">Introducción a la terapia CPAP</h1>
            {/* <p className="text-sm text-muted-foreground">Tu guía esencial en 3 pasos</p> */}
          </div>

          {/* Hero Section - Simplified */}
          <Card className="mb-4 sm:mb-6 animate-fade-in">
            <CardContent className="p-6 sm:p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-primary text-center pb-3">
                      ¿Qué es la terapia CPAP?
                    </h2>
                    <p className="text-muted-foreground mb-0">
                      Es el tratamiento más efectivo para la apnea obstructiva del sueño.<br/><br/>Envía aire presurizado 
                      a través de una mascarilla, manteniendo tus vías respiratorias abiertas durante el sueño.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3 pt-2">
                  {beneficiosClave.map((beneficio, index) => <div key={index} className="flex items-center gap-3 sm:flex-col sm:text-center sm:gap-2">
                      <div className="flex h-full">
                        <div style={{'marginTop': '3px'}}>
                          {beneficio.icon}
                        </div>                          
                        
                      </div>
                      <div className="flex-1 sm:flex-none">
                        <h4 className="font-medium text-md mb-1">{beneficio.titulo}</h4>
                        <p className="text-sm text-muted-foreground">{beneficio.descripcion}</p>
                      </div>
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Components Section - Progressive Disclosure */}
          <Card className="mb-4 sm:mb-6">
            <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-6">
              <h2 className="text-xl font-semibold text-primary mb-2 text-center">
                  Componentes de la terapia CPAP
              </h2>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
              {componentesEsenciales.map((componente, index) => <div key={componente.id} className="border rounded-lg">
                  <Collapsible open={expandedSection === componente.id} onOpenChange={() => toggleSection(componente.id)}>
                    <CollapsibleTrigger className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-accent/5 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="overflow-hidden w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white shadow-sm border flex items-center justify-center p-1">
                          <img src={componente.imagen} alt={componente.titulo} className="w-full h-full object-contain" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-foreground">{componente.titulo}</h3>
                          <p className="text-sm text-muted-foreground">{componente.subtitle}</p>
                        </div>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${expandedSection === componente.id ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent className="border-t bg-soft-bg/30">
                      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                        <p className="text-muted-foreground text-sm">
                          {componente.descripcionCorta}
                        </p>
                        
                        {componente.caracteristicas && <div className="space-y-2">
                            <h4 className="font-medium text-sm">Características principales:</h4>
                            <div className="grid gap-3">
                              {componente.caracteristicas.map((caracteristica, idx) => <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <div className="flex h-full">
                                    <CheckCircle className="h-3 w-3 text-success" style={{'minWidth': '15px', 'minHeight': '15px'}} />
                                  </div>
                                  {caracteristica}
                                </div>)}
                            </div>
                          </div>}
                        
                        {componente.tipos && <div className="space-y-3">
                            <h4 className="font-medium text-sm">Tipos de mascarillas recomendadas para iniciar:</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-2">
                              {componente.tipos.map((tipo, idx) => <div key={idx} className="border rounded-lg overflow-hidden bg-background">
                                  <div className="overflow-hidden aspect-video sm:aspect-square bg-white p-2">
                                    <img src={tipo.imagen} alt={tipo.nombre} className="w-full h-full object-contain" />
                                  </div>
                                  <div className="p-3 sm:p-2">
                                    <div className="flex items-center justify-between mb-2 sm:mb-1">
                                      <h5 className="font-medium text-sm">{tipo.nombre}</h5>
                                      {/* <Badge variant="secondary" className="text-xs px-2 py-1 sm:px-1 sm:py-0">
                                        {tipo.recomendada}
                                      </Badge> */}
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed sm:leading-tight">
                                      {tipo.descripcion}
                                    </p>
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>)}
            </CardContent>
          </Card>

          {/* Success Formula */}
          <Card className="mb-4 sm:mb-6">
            <CardContent className="p-4 sm:p-6">
              <div className="text-center space-y-4">
                <h3 className="text-xl font-bold text-primary">
                  Fórmula del éxito
                </h3>
                <div className="flex items-center justify-center gap-2 sm:gap-4">
                  <div className="overflow-hidden w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-white shadow-sm border flex items-center justify-center p-1 sm:p-2">
                    <img src="https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YH680B_1.webp" alt="Equipo CPAP" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-xl sm:text-2xl font-bold text-muted-foreground">+</span>
                  <div className="overflow-hidden w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-white shadow-sm border flex items-center justify-center p-1 sm:p-2">
                    <img src="https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YN-03%20(Nasal).webp" alt="Mascarilla CPAP" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-xl sm:text-2xl font-bold text-muted-foreground">=</span>
                  <div className="overflow-hidden w-16 h-16 sm:w-20 sm:h-20 border rounded-lg bg-success/10 flex items-center justify-center">
                    <Star className="h-8 w-8 sm:w-16 sm:h-16 text-success" />
                  </div>
                </div>
                <div className="max-w-full">
                  <p className="text-muted-foreground text-left">
                    La combinación correcta de <strong>equipo + mascarilla personalizada</strong> garantiza 
                    una terapia exitosa y cómoda desde el primer día.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <Card>
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="max-w-full mx-auto space-y-4">
                <h3 className="text-xl font-bold text-primary">
                  ¿Listo para encontrar tus componentes ideales?
                </h3>
                <p className="text-muted-foreground text-left">
                  Nuestro cuestionario te ayudará a encontrar los componentes perfectos para ti.
                </p>
                <div className="bg-accent/10 border border-accent/20 rounded-md p-3">
                  <p className="text-accent font-medium text-sm">
                    ✨ <strong>buno+</strong> te acompañará en cada paso del proceso
                  </p>
                </div>
                <Button className="min-h-touch px-6 sm:px-8 gap-0 w-full text-lg" onClick={() => navigate('/eleccion')}>
                  <span className="">Encontrar componentes</span>
                  <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>;
}